import { inject } from "fw";
import { Store, handle } from "fw-state";

import { LocalStorageCache } from "caching";
import { once } from "helpers/once";

import { AccountType, AtsOrganization } from "models/ats-organization";
import {
    Filter,
    MarketingOutreachSentBase,
    MarketingOutreachSentResult,
    MarketingOutreachSentSource,
    MarketingOutreachSentStatusName,
    OrganizationFilter,
    OrganizationIdData,
    StatusAggregationItem
} from "models/monitor-email";

import { AtsOrgSummaryRepository } from "network/ats-org-summary-repository";
import { AtsRepository } from "network/ats-repository";
import { MarketingRepository } from "network/marketing-repository";

import { DateService } from "service/date-service";

import { LogoutAction, StartAction } from "state/actions";
import { CurrentUserStore } from "state/current-user";

const DEFAULT_PAGE_SIZE = 20;
const CACHE_LABEL = "ats-monitoring-emails-store";
const ISO_DATE_FORMAT_LOCALE = "en-CA";

interface MonitorEmailShape {
    collapsedItem: MarketingOutreachSentBase;
    currentUserId: string;
    errorLoading: boolean;
    filter: Filter;
    list: MarketingOutreachSentBase[];
    loaded: boolean;
    orgFilter: OrganizationFilter;
    orgIdsNamesMap: OrganizationIdData[];
    pageNumber: number;
    pageSize: number;
    recipients: any[];
    search: string;
    sort: string;
    statusCounts: StatusAggregationItem[];
    total: number;
}

export class FetchListAction {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = { type: null, tags: null },
        public sort: string = ""
    ) {}
}

export class EmailsEnsureAction {}
export class EmailsRefreshAction {}
export class NextPageAction {}
export class PreviousPageAction {}

export class CollapseItemAction {
    constructor(public id: string) {}
}

type SavedMonitorEmailState = {
    filter: Filter;
    search: string;
    sort: string;
    pageNumber: number;
    pageSize: number;
    orgFilter: OrganizationFilter;
};

@inject
export class MonitorEmailStore extends Store<MonitorEmailShape> {
    private currentAbortController: AbortController | null = null;
    private currentSingleItemAbortController: AbortController | null = null;

    constructor(
        private atsOrgSummaryRepository: AtsOrgSummaryRepository,
        private atsRepo: AtsRepository,
        private cache: LocalStorageCache,
        private currentUserStore: CurrentUserStore,
        private dateSvc: DateService,
        private marketingRepo: MarketingRepository
    ) {
        super();
    }

    defaultFilter() {
        return {};
    }

    defaultState() {
        return {
            collapsedItem: null,
            currentUserId: null,
            errorLoading: false,
            filter: this.defaultFilter(),
            list: [],
            loaded: false,
            orgFilter: { type: AccountType.Customer, tags: null },
            orgIdsNamesMap: [],
            pageNumber: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            recipients: [],
            search: null,
            sort: "SentAt DESC",
            statusCounts: [],
            total: 0
        };
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:${CACHE_LABEL}`;
    }

    saveState() {
        if (this.state.currentUserId == null) return;

        const saveState: SavedMonitorEmailState = {
            filter: this.state.filter,
            orgFilter: this.state.orgFilter,
            pageNumber: this.state.pageNumber,
            pageSize: this.state.pageSize,
            search: this.state.search,
            sort: this.state.sort
        };
        this.cache.set<SavedMonitorEmailState>(this.cacheKey, saveState);
    }

    restoreState() {
        if (this.state.currentUserId == null) return;

        const savedState = this.cache.get<SavedMonitorEmailState>(this.cacheKey);
        if (savedState) {
            this.setState((state) => ({
                ...state,
                filter: savedState.filter,
                orgFilter: savedState.orgFilter,
                pageNumber: savedState.pageNumber || 1,
                pageSize: savedState.pageSize || DEFAULT_PAGE_SIZE,
                search: savedState.search,
                sort: savedState.sort
            }));
        }
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    @handle(CollapseItemAction)
    private collapseItemAction(action: CollapseItemAction) {
        this.setState((state) => ({
            ...state,
            collapsedItem: null,
            recipients: [],
            errorLoading: false
        }));

        if (action.id) {
            this.loadSingleItemData(action.id);
        }
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.setState(() => this.defaultState());
    }

    @handle(EmailsEnsureAction)
    private async handleEmailsEnsureAction() {
        await once(this.cacheKey, async () => {
            if (this.state.loaded) {
                return;
            }

            this.restoreState();
        });
    }

    @handle(EmailsRefreshAction)
    private handleRefreshAction(action: EmailsRefreshAction) {
        if (!this.state.loaded) return;

        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        this.loadPageData();
    }

    @handle(FetchListAction)
    private async handleFetchList(action: FetchListAction) {
        // transform empty array to null to ensure orgFilter is working correctly
        if (0 === action.orgFilter?.tags?.length) action.orgFilter.tags = null;

        this.setState(() => ({
            ...this.state,
            currentUserId: this.state.currentUserId,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            loaded: false,
            errorLoading: false,
            pageNumber: 1,
            orgFilter: action.orgFilter
        }));

        await this.loadPageData();
    }

    @handle(NextPageAction)
    private async handleNextPageAction(action: NextPageAction) {
        const { pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber + 1
        }));

        await this.loadPageData();
    }

    @handle(PreviousPageAction)
    private async handlePreviousPage(s: PreviousPageAction) {
        const { pageNumber } = this.state;
        if (pageNumber <= 1) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber - 1
        }));

        await this.loadPageData();
    }

    private convertDateRangeForMarketingApi(filter: any): { sentAtFrom?: string; sentAtTo?: string } {
        const dateRange = this.dateSvc.parseDateRange(filter.range);
        const startDate: string = dateRange?.startDate || "";
        const endDate: string = dateRange?.endDate || "";

        let fromDate: Date;
        let toDate: Date;

        if (startDate) {
            fromDate = this.dateSvc.createUTCDate(startDate);
        } else {
            const dateProxy = new Date();
            dateProxy.setHours(0, 0, 0, 0);
            fromDate = this.dateSvc.createUTCDate(dateProxy.toLocaleDateString(ISO_DATE_FORMAT_LOCALE));
        }

        if (endDate) {
            toDate = this.dateSvc.createUTCDate(endDate, true);
        } else {
            const dateProxy = new Date();
            dateProxy.setHours(0, 0, 0, 0);
            toDate = this.dateSvc.createUTCDate(this.dateSvc.addDaysToDate(dateProxy, 1).toLocaleDateString(ISO_DATE_FORMAT_LOCALE), true);
        }

        const result: { sentAtFrom?: string; sentAtTo?: string } = {};
        if (fromDate && !!fromDate?.getTime()) {
            result.sentAtFrom = this.dateSvc.formatDateTime(fromDate);
        }
        if (toDate && !!toDate.getTime()) {
            result.sentAtTo = this.dateSvc.formatDateTime(toDate);
        }

        return result;
    }

    private pluckOrgIdsFromOutreachSents(outreachSents: MarketingOutreachSentBase[]): string[] {
        return (outreachSents || []).map((os) => os.AtsOrgId).filter((id) => !!id);
    }

    private getUniqueOrgIds(orgIds: string[]): string[] {
        return [...new Set(orgIds)];
    }

    private getOrgIdsNotInMap(uniqueOrgIds: string[], existingOrgIdsDataMap: OrganizationIdData[]): string[] {
        const existingIds = new Set(existingOrgIdsDataMap.map((org) => org.id));
        return uniqueOrgIds.filter((id) => !existingIds.has(id));
    }

    private buildOrganizationFilterString(orgIds: string[]): string {
        if (orgIds.length === 0) return "";
        return `Id:(${orgIds.join(" OR ")})`;
    }

    private enrichOutreachSentsWithOrgData(outreachSents: MarketingOutreachSentBase[], orgIdsDataMap: OrganizationIdData[]): MarketingOutreachSentBase[] {
        const orgMap = new Map(orgIdsDataMap.map((org) => [org.id, { name: org.name, timezone: org.timezone }]));

        return outreachSents.map((item) => {
            const orgData = orgMap.get(item.AtsOrgId);
            return {
                ...item,
                AtsOrgName: orgData?.name || item.AtsOrgId,
                AtsOrgTimezone: orgData?.timezone || "UTC"
            };
        });
    }

    private async fetchOrgInfoForOutreachSents(outreachSents: MarketingOutreachSentBase[]): Promise<OrganizationIdData[]> {
        const orgIdsInResponse = this.pluckOrgIdsFromOutreachSents(outreachSents);
        const uniqueOrgIds = this.getUniqueOrgIds(orgIdsInResponse);
        const missingOrgIds = this.getOrgIdsNotInMap(uniqueOrgIds, this.state.orgIdsNamesMap);

        // fetch new organization data
        let updatedOrgIdsDataMap = [...this.state.orgIdsNamesMap];
        if (missingOrgIds.length > 0) {
            try {
                const orgFilterString = this.buildOrganizationFilterString(missingOrgIds);
                const organizationsResponse = await this.atsRepo.organizations(orgFilterString);

                const fetchedOrgs: OrganizationIdData[] = organizationsResponse?.list?.map((org: AtsOrganization) => ({
                    id: org?.Id,
                    name: org?.Name,
                    timezone: org?.Timezone
                }));

                updatedOrgIdsDataMap = [...updatedOrgIdsDataMap, ...fetchedOrgs];
            } catch (orgFetchError) {
                console.error("Error fetching organizations:", orgFetchError);
            }
        }

        return updatedOrgIdsDataMap;
    }

    private fetchOrgTypeIds(): Promise<string[] | null> {
        const { type } = this.state.orgFilter;
        if (type === null) return null;

        return this.atsOrgSummaryRepository.getOrgsOfType(type);
    }

    private fetchOrgTagIds(): Promise<string[] | null> {
        const { tags } = this.state.orgFilter;
        if (!tags?.length) return null;

        return this.atsOrgSummaryRepository.getOrgsWithTags(tags);
    }

    private fetchOutreachSents(params): Promise<MarketingOutreachSentResult> {
        return this.marketingRepo.marketingOutreachSents(params);
    }

    private fetchStatusCounts(params): Promise<any> {
        return this.marketingRepo.marketingOutreachSentsStatusCounts(params);
    }

    private loadEmptyList() {
        this.setState((state) => ({
            ...state,
            errorLoading: false,
            list: [],
            loaded: true,
            statusCounts: [],
            total: 0
        }));
    }

    private async loadPageData() {
        // cancel any existing requests
        if (this.currentAbortController) {
            this.currentAbortController.abort();
        }

        // allow cancellation of these requests
        this.currentAbortController = new AbortController();
        const signal = this.currentAbortController.signal;

        const { filter, orgFilter, pageNumber, pageSize, search, sort } = this.state;

        try {
            this.setState((state) => ({ ...state, loaded: false, errorLoading: false }));

            // determine which orgs to fetch based on filter selections
            let orgTypeIds: string[] = [];
            let orgTagsIds: string[] = [];
            await Promise.allSettled([this.fetchOrgTypeIds(), this.fetchOrgTagIds()])
                .then(([fetchedOrgTypeIds, fetchedOrgTagIds]: [PromiseSettledResult<any>, PromiseSettledResult<any>]) => {
                    orgTypeIds = "fulfilled" === fetchedOrgTypeIds?.status ? fetchedOrgTypeIds?.value || [] : [];
                    orgTagsIds = "fulfilled" === fetchedOrgTagIds?.status ? fetchedOrgTagIds?.value || [] : [];
                })
                .catch((error) => console.error(error));

            // bail if request was cancelled
            if (signal.aborted) return;

            const specificOrgId = filter?.organization?.Id;
            const hasSpecificOrg = !!specificOrgId;
            const hasOrgType = orgFilter?.type !== null;
            const hasOrgTags = orgFilter?.tags !== null;

            let targetOrgIds: string[] = [];
            if (hasSpecificOrg) {
                targetOrgIds = [specificOrgId];
            }

            // intersect: org type filters
            if (hasOrgType) {
                if (0 === targetOrgIds.length) {
                    targetOrgIds = orgTypeIds;
                } else {
                    targetOrgIds = targetOrgIds.filter((orgId) => orgTypeIds.includes(orgId));
                }
                if (0 === targetOrgIds.length) {
                    this.loadEmptyList();
                    return;
                }
            }

            // intersect: org tags filters
            if (hasOrgTags) {
                if (0 === targetOrgIds.length) {
                    targetOrgIds = orgTagsIds;
                } else {
                    targetOrgIds = targetOrgIds.filter((orgId) => orgTagsIds.includes(orgId));
                }
                if (0 === targetOrgIds.length) {
                    this.loadEmptyList();
                    return;
                }
            }

            // prepare params for API calls
            /* jshint eqnull:true */
            const status = null != filter?.status ? MarketingOutreachSentStatusName[filter?.status] : null; // coerce undefined to null
            /* jshint eqnull:true */
            const source = null != filter?.outreachSentSource ? MarketingOutreachSentSource[filter?.outreachSentSource] : null; // coerce undefined to null
            const dateRange = this.convertDateRangeForMarketingApi(filter);

            // assemble statusCounts params
            const marketingOutreachSentsStatusCountsParams = {
                atsOrgIds: targetOrgIds,
                // isOutreach: true, // @todo: maybe later
                // isDirect: true,   // @todo: maybe later
                mediaTypes: "email",
                recordTypes: "applications,contacts",
                search: search || "",
                signal, // abort signal
                source,
                timezone: this.currentUserStore.state.timezone,
                ...dateRange
            };

            // assemble outreachSent params
            const marketingOutreachSentsParams = {
                ...marketingOutreachSentsStatusCountsParams,
                includeCampaign: true,
                includeFailedDeliveries: true,
                limit: pageSize,
                page: pageNumber,
                sort,
                status
            };

            let list: MarketingOutreachSentBase[] = [];
            let total: number = 0;
            let marketingStatusCounts: MarketingOutreachSentStatusName = null;
            await Promise.allSettled([this.fetchOutreachSents(marketingOutreachSentsParams), this.fetchStatusCounts(marketingOutreachSentsStatusCountsParams)])
                .then(([fetchedOutreachSents, fetchedStatusCounts]: [PromiseSettledResult<any>, PromiseSettledResult<any>]) => {
                    if ("fulfilled" === fetchedOutreachSents?.status) {
                        list = fetchedOutreachSents?.value?.list;
                        total = fetchedOutreachSents?.value?.total;
                    }
                    if ("fulfilled" === fetchedStatusCounts?.status) {
                        marketingStatusCounts = fetchedStatusCounts?.value;
                    }
                })
                .catch((error) => console.error(error));

            // bail if request was cancelled
            if (signal.aborted) return;

            // transform the data into a format that the <status-counts> component expects
            const statusCounts: StatusAggregationItem[] = Object.values(MarketingOutreachSentStatusName).map((statusName, index) => ({
                Key: index,
                Total: marketingStatusCounts[statusName] || 0
            }));

            // add org names to outreach sents
            const updatedOrgIdsDataMap = await this.fetchOrgInfoForOutreachSents(list);

            // bail if request was cancelled
            if (signal.aborted) return;

            const enrichedList = this.enrichOutreachSentsWithOrgData(list, updatedOrgIdsDataMap);

            this.setState((state) => ({
                ...state,
                loaded: true,
                statusCounts,
                list: enrichedList,
                total,
                orgIdsNamesMap: updatedOrgIdsDataMap
            }));

            this.saveState();
        } catch (err) {
            if (err.name === "AbortError" || signal.aborted) {
                // request was cancelled because a new request was sent, don't update state
                return;
            }
            console.log(err);
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private async loadSingleItemData(id: string) {
        // cancel any existing requests
        if (this.currentSingleItemAbortController) {
            this.currentSingleItemAbortController.abort();
        }

        // allow cancellation of these requests
        this.currentSingleItemAbortController = new AbortController();
        const signal = this.currentSingleItemAbortController.signal;

        const { list } = this.state;
        const outreachSent = list?.find((item) => item.Id === id);
        if (!Object.keys(outreachSent || {}).length) {
            return;
        }

        const outreachSentReq = this.marketingRepo.marketingOutreachSentById(id, signal);
        const outreachSentRecipientsReq = this.marketingRepo.marketingOutreachDeliveryList({ outreachSentId: id, signal });
        try {
            const [outreachSent, outreachSentRecipients] = await Promise.all([outreachSentReq, outreachSentRecipientsReq]);

            // bail if request was cancelled
            if (signal.aborted) return;

            const dataBag = {};
            if (outreachSent) {
                dataBag["collapsedItem"] = outreachSent;
            }
            if (outreachSentRecipients) {
                dataBag["recipients"] = outreachSentRecipients || [];
            }

            this.setState((state) => ({
                ...state,
                ...dataBag
            }));
        } catch (err) {
            if (err.name === "AbortError" || signal.aborted) {
                // request was cancelled because a new request was sent, don't update state
                return;
            }
            console.error("Error loading outreach sent item:", err);
            this.setState((state) => ({
                ...state,
                collapsedItem: null,
                errorLoading: true
            }));
        }
    }
}
